import React, { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Stack, Grid, Button, IconButton, Tooltip, ButtonBase, Chip } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import api from '../services/api';
import { useShell } from '../components/Layout';
import MarketplaceIcon from '../components/common/MarketplaceIcon';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { formatDate } from '../utils/format';
import { hasSync } from '../utils/marketplaces';
import { marketplaceMeta, tokens } from '../theme';

// Section "Подключения": the marketplace's accounts, their keys and data loading.
const MarketplaceConnectionsPage = () => {
  const { marketplace, accounts } = useOutletContext();
  const { openConnect, reloadShell } = useShell();
  const navigate = useNavigate();
  const [toDelete, setToDelete] = useState(null);
  const meta = marketplaceMeta(marketplace);

  return (
    <Box>
      <Grid container spacing={2.5}>
        {accounts.map((a) => (
          <Grid item xs={12} md={6} xl={4} key={a.id}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <MarketplaceIcon marketplace={marketplace} size={44} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h3" noWrap title={a.name}>{a.name || 'Без названия'}</Typography>
                    <Typography variant="body2" color="text.secondary">подключён {formatDate(a.created_at)}</Typography>
                  </Box>
                  <Tooltip title="Изменить название или ключ">
                    <IconButton onClick={() => openConnect(marketplace, a, () => {})} aria-label={`Изменить кабинет ${a.name}`}><EditOutlinedIcon /></IconButton>
                  </Tooltip>
                  <Tooltip title="Отключить кабинет">
                    <IconButton onClick={() => setToDelete(a)} aria-label={`Отключить кабинет ${a.name}`} sx={{ color: tokens.error }}><DeleteOutlineRoundedIcon /></IconButton>
                  </Tooltip>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip size="small" icon={<VerifiedUserOutlinedIcon />} label="доступ проверен" sx={{ bgcolor: tokens.successSoft, color: tokens.success, '& .MuiChip-icon': { color: tokens.success } }} />
                  {!hasSync(marketplace) && <Chip size="small" label="загрузка данных — скоро" sx={{ bgcolor: tokens.infoSoft, color: tokens.info }} />}
                </Stack>
                <Box sx={{ flex: 1 }} />
                {hasSync(marketplace) && (
                  <Button variant="outlined" startIcon={<SyncRoundedIcon />} onClick={() => navigate(`/marketplaces/sync/${a.id}`)} sx={{ alignSelf: 'flex-start' }}>
                    Ход загрузки данных
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
        <Grid item xs={12} md={6} xl={4}>
          <ButtonBase onClick={() => openConnect(marketplace)}
            sx={{ width: '100%', height: '100%', minHeight: 180, borderRadius: '16px', border: `2px dashed ${tokens.borderStrong}`, display: 'flex', flexDirection: 'column', gap: 1,
              color: tokens.primaryDark, bgcolor: 'transparent', transition: 'all .2s', '&:hover': { borderColor: tokens.primary, bgcolor: tokens.primaryTint } }}>
            <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: tokens.primarySoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AddRoundedIcon />
            </Box>
            <Typography sx={{ fontWeight: 700 }}>Добавить кабинет {meta.label}</Typography>
            <Typography variant="body2" color="text.secondary">например, второе юрлицо или магазин</Typography>
          </ButtonBase>
        </Grid>
      </Grid>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        API-ключи хранятся на сервере в зашифрованном виде и больше не показываются. Чтобы сменить ключ, нажмите «Изменить».
      </Typography>
      <ConfirmDialog
        open={Boolean(toDelete)}
        title="Отключить кабинет?"
        text={toDelete ? `Кабинет «${toDelete.name}» будет отключён. Загруженные данные, отчёты и себестоимость этого кабинета удалятся без возможности восстановления.` : ''}
        confirmLabel="Отключить"
        danger
        onConfirm={() => api.delete(`/marketplace/accounts/${toDelete.id}`)}
        onClose={(done) => { setToDelete(null); if (done) reloadShell(); }}
      />
    </Box>
  );
};

export default MarketplaceConnectionsPage;
